import useCancelOrders, {
  CancelOrderResponse,
} from '@/features/trade/order/api/cancel-orders';
import {
  createOrder,
  createOrderInputSchema,
  CreateOrderResponse,
} from '@/features/trade/order/api/create-order';
import { z } from 'zod';
import { COLLATERAL_DECIMALS, MarketSpec } from '@/store/use-markets-store';
import { ErrorCode, ErrorMsgs, OrderStatus, OrderType } from '@/types/enums';
import { Order, useOrdersStore } from '@/store/use-orders-store';
import { toast } from 'sonner';
import {
  bigIntToDecimalStr,
  parseInputToDecimalStr,
} from '@/utils/value-format';
import { useAccountStore } from '@/store/use-account-store';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { usePrivyAccount } from '@/hooks';

export const CreateOrderInputSchemaWithId = createOrderInputSchema.extend({
  id: z.string(),
  isSubmitted: z.boolean(),
});

type CreateOrderInputWithId = z.infer<typeof CreateOrderInputSchemaWithId>;

// // for one symbol
// export const updateOrders = ;

type UpdateOrdersParams = {
  currentOrders: CreateOrderInputWithId[];
  updatedOrders: CreateOrderInputWithId[];
  marketSpec: MarketSpec;
};

type QueueItem =
  | {
      type: OrderStatus.NEW;
      promise: Promise<CreateOrderResponse>;
    }
  | {
      type: OrderStatus.CANCELLED;
      promise: Promise<CancelOrderResponse>;
    };

type QueueResult =
  | {
      type: OrderStatus.NEW;
      value: CreateOrderResponse;
    }
  | {
      type: OrderStatus.CANCELLED;
      value: CancelOrderResponse;
    };

export const mapOrderToCreateOrderInput = (
  order: Order,
  marketSpec: Pick<MarketSpec, 'priceDecimals' | 'sizeDecimals'>,
  isSubmitted: boolean,
): CreateOrderInputWithId => {
  return {
    id: order.id,
    symbol: order.symbol,
    isBuy: order.isBuy,
    orderType: order.orderType,
    size: bigIntToDecimalStr(order.size, marketSpec.sizeDecimals),
    limitPrice: bigIntToDecimalStr(order.limitPrice, marketSpec.priceDecimals),
    initMarginRatio: bigIntToDecimalStr(
      order.initMarginRatio,
      COLLATERAL_DECIMALS,
    ),
    tpPrice: order.tpPrice
      ? bigIntToDecimalStr(order.tpPrice, marketSpec.priceDecimals)
      : undefined,
    slPrice: order.slPrice
      ? bigIntToDecimalStr(order.slPrice, marketSpec.priceDecimals)
      : undefined,
    reduceOnly: order.reduceOnly,
    isSubmitted,
  };
};

export default function useUpdateOrders() {
  const { mutateAsync: cancelOrders } = useCancelOrders();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      currentOrders,
      updatedOrders,
      marketSpec,
    }: UpdateOrdersParams) => {
      const queue: QueueItem[] = [];

      // -- New Orders --
      updatedOrders.forEach((o) => {
        if (currentOrders.some((co) => co.id === o.id)) return;
        const validatedCreateOrderParams = {
          ...o,
          limitPrice: parseInputToDecimalStr(
            o.limitPrice,
            marketSpec.priceDecimals,
          ),
          size: parseInputToDecimalStr(o.size, marketSpec.sizeDecimals),
          tpPrice: o.tpPrice
            ? parseInputToDecimalStr(o.tpPrice, marketSpec.priceDecimals)
            : undefined,
          slPrice: o.slPrice
            ? parseInputToDecimalStr(o.slPrice, marketSpec.priceDecimals)
            : undefined,
        };
        queue.push({
          type: OrderStatus.NEW,
          promise: createOrder({
            order: validatedCreateOrderParams,
            showToast: false,
          }),
        });
      });

      const toEdit = updatedOrders.filter((uo) =>
        currentOrders.find(
          (co) =>
            co.id === uo.id &&
            (co.limitPrice !== uo.limitPrice ||
              co.size !== uo.size ||
              co.tpPrice !== uo.tpPrice ||
              co.slPrice !== uo.slPrice),
        ),
      );

      const cancelledIds: string[] = [
        ...toEdit.map((o) => o.id),
        ...currentOrders
          .filter((co) => !updatedOrders.find((uo) => uo.id === co.id))
          .map((o) => o.id),
      ];

      if (cancelledIds.length > 0) {
        queue.push({
          type: OrderStatus.CANCELLED,
          promise: cancelOrders({ id: cancelledIds, showToast: false }),
        });
      }

      toEdit.forEach((o) => {
        const { id, ...createOrderParams } = o;
        const validatedCreateOrderParams = {
          ...createOrderParams,
          limitPrice: parseInputToDecimalStr(
            createOrderParams.limitPrice,
            marketSpec.priceDecimals,
          ),
          size: parseInputToDecimalStr(
            createOrderParams.size,
            marketSpec.sizeDecimals,
          ),
          tpPrice: createOrderParams.tpPrice
            ? parseInputToDecimalStr(
                createOrderParams.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
          slPrice: createOrderParams.slPrice
            ? parseInputToDecimalStr(
                createOrderParams.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        };
        queue.push({
          type: OrderStatus.NEW,
          promise: createOrder({
            order: validatedCreateOrderParams,
            showToast: false,
          }),
        });
      });

      // Chain all requests to ensure nonce monotonicity and return array of promise results
      const chainedPromises = queue.reduce(
        (acc: Promise<QueueResult[]>, curr) =>
          acc.then((results) =>
            curr.promise.then((result) => [
              ...results,
              { type: curr.type, value: result } as QueueResult,
            ]),
          ),
        Promise.resolve([]),
      );

      const toastPromise = chainedPromises.then((results) => {
        return new Promise<void>((resolve, reject) => {
          let cancelledIds = new Set(
            results.flatMap((res) => {
              if (res.type !== OrderStatus.CANCELLED) return [];
              return res.value.cancelledIds;
            }),
          );
          let newIds = new Set(
            results.reduce((prev, curr) => {
              if (curr.type !== OrderStatus.NEW) return prev;
              return [...prev, curr.value.id];
            }, [] as string[]),
          );

          let subscriberTimeoutId: NodeJS.Timeout;
          let unsubscribe: () => void;

          const cleanup = () => {
            clearTimeout(subscriberTimeoutId);
            unsubscribe();
          };
          const { address, getAccurateTime } = useAccountStore.getState();

          unsubscribe = useOrdersStore.subscribe((state) => {
            const { newOrderIds, rejectedOrderIds, cancelledOrderIds } =
              state.getOrders(address);
            const confirmedCancels =
              cancelledIds.intersection(cancelledOrderIds);
            const confirmedCreates = newIds.intersection(newOrderIds);

            cancelledIds = cancelledIds.difference(confirmedCancels);
            newIds = newIds.difference(confirmedCreates);

            if (cancelledIds.size === 0 && newIds.size === 0) {
              cleanup();
              resolve();
            } else if (
              Array.from(cancelledIds.union(newIds)).some((id) =>
                rejectedOrderIds.has(id),
              )
            ) {
              cleanup();
              reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT); // TODO: better error handling
            }
          });

          subscriberTimeoutId = setTimeout(() => {
            cleanup();
            if (cancelledIds.size === 0 && newIds.size === 0) {
              resolve();
            } else {
              reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
            }
          }, 30_000); // 30 seconds timeout

          const cancelOrdersToPoll: [string, OrderStatus, number][] =
            Array.from(cancelledIds).map((id) => [
              id,
              OrderStatus.CANCELLED,
              getAccurateTime(),
            ]);
          const newOrdersToPoll: [string, OrderStatus, number][] = Array.from(
            newIds,
          ).map((id) => [id, OrderStatus.NEW, getAccurateTime()]);
          useOrdersStore
            .getState()
            .pollOrders([...cancelOrdersToPoll, ...newOrdersToPoll]);
        });
      });

      const isLimitOrders = currentOrders.every(
        (co) => co.orderType === OrderType.LIMIT,
      );

      toast.promise(toastPromise, {
        loading: t(
          isLimitOrders
            ? 'updateOrders.updatingLimitOrder'
            : 'updateOrders.updatingTpsl',
        ),
        success: t(
          isLimitOrders
            ? 'updateOrders.limitOrderUpdated'
            : 'updateOrders.tpslUpdated',
        ),
        error: (error) =>
          t(
            isLimitOrders
              ? 'updateOrders.limitOrderUpdateError'
              : 'updateOrders.tpslUpdateError',
            {
              errorMessage: ErrorMsgs[error as ErrorCode],
            },
          ),
      });

      return chainedPromises;
    },
  });
}
